import {
  GetPostQuery,
  HeroBannerFragmentFragment,
  ImageFragmentFragment,
  MainHeroFragmentFragment,
} from '../__generated__/graphql';
import { AcfFragment } from '../content-blocks/types';

interface DefaultT {
  id: string;
  parentId?: null | string;
}

interface Options<T extends DefaultT> {
  idKey?: keyof T;
  parentKey?: keyof T;
}

type Fragment<T> = {
  children: Fragment<T>[];
} & T;

// @faustwp/core' exports flatListToHierarchical but that one is not strongly typed
// and simply returns Record<string | number, unknown>[]
export const flatListToHierarchical = <T extends DefaultT>(
  data: T[],
  {
    idKey = 'id',
    parentKey = 'parentId',
  }: Options<T> = {},
): Fragment<T>[] => {
  const tree: Fragment<T>[] = [];
  const childrenOf: Record<number | string, Fragment<T>[]> = {};

  for (const item of data) {
    const newItem: Fragment<T> = { ...item, children: [] };
    const { [idKey]: id, [parentKey]: parentId = 0 } = newItem;
    if (id != null && typeof id === 'string') {
      childrenOf[id] = childrenOf[id] ?? [];
      newItem.children = childrenOf[id];

      if (parentId != null && (typeof parentId === 'string' || typeof parentId === 'number')) {
        (childrenOf[parentId] ??= []).push(newItem);
      }
      else {
        tree.push(newItem);
      }
    }
  }

  return tree;
};

export const getHeroImage = (acfFragments: AcfFragment[]): ImageFragmentFragment | null => {
  const mainHero = acfFragments
    .find(block => block.__typename === 'AcfMainhero') as MainHeroFragmentFragment | undefined;

  if (mainHero?.mainHero?.image?.node.sourceUrl) {
    return mainHero.mainHero.image;
  }

  const heroBanner = acfFragments
    .find(block => block.__typename === 'AcfHerobanner') as HeroBannerFragmentFragment | undefined;

  if (heroBanner?.heroBanner?.image?.node.sourceUrl) {
    return heroBanner.heroBanner.image;
  }

  return null;
};

export const getPostFeatureImage = (post: GetPostQuery['post']) => {
  if (!post) {
    return null;
  }

  const { featuredImage } = post;

  if (!featuredImage) {
    return null;
  }

  if (!featuredImage.node.sourceUrl) {
    return null;
  }

  /* eslint-disable @typescript-eslint/naming-convention */
  const imageFragment: ImageFragmentFragment = {
    __typename: featuredImage.__typename as ImageFragmentFragment['__typename'],
    node: {
      __typename: featuredImage.node.__typename,
      altText: featuredImage.node.altText,
      id: featuredImage.node.id,
      link: featuredImage.node.link,
      mediaDetails: {
        ...featuredImage.node.mediaDetails,
      },
      sourceUrl: featuredImage.node.sourceUrl,
    },
  };
  /* eslint-enable @typescript-eslint/naming-convention */

  return imageFragment;
};
